// src/prototype/components/hub/BriefTab.tsx
// ============================================================
// DECISION BRIEF TAB (Full-Content Drilldown v4)
//
// v4 brief enrichment: Strategic Alignment panel, risks with category chips, a
// dependency-aware milestone roadmap (months-from-start + value notes), and an
// Implementation Requirements section (data/technology/people/governance) — plus a
// 4-dimension scoring profile rail and a financial-model summary that opens the ROI tab.
//
// Narrative prose (summary/steps/context/description/opImpact) still comes from
// getDecisionBrief; the structured content comes from the BigQuery-shaped adapter.
// ============================================================

import React from 'react';
import {
  FileText, Zap, Target, BookOpen, Shield, Calendar, List, TrendingUp,
  BarChart3, Users, Scale, ArrowRight, ArrowLeft,
} from 'lucide-react';

import type { DecisionBrief } from '../../data/decisionBrief';
import { TOTAL_PORTFOLIO_SIZE } from '../../data/decisionBrief';
import { LIBRARY_SIZE } from '../../data/useCaseLibrary';
import {
  getScores, getRisks, getMilestones, getRequirements, getAlignment, DIM_META,
} from '../../data/adapters';
import type { MilestoneVM, RequirementType } from '../../data/adapters';
import { BandedCard } from './BandedCard';
import { Collapse } from '../Collapse';
import { StepList } from '../StepList';
import { AsOfChip } from './AsOfChip';
import { VerdictPill } from '../VerdictPill';
import { SectionHeaderBand } from './SectionHeaderBand';
import { parseDecisionText } from '../../data/decisionText';
import { hubFigures } from '../../roi/hubFigures';
import { CANON_CONFIDENCE_BASIS } from '../../roi/roiCanon';

const confPill = (c: number) => (c >= 0.65 ? 'bg-emerald-50 text-emerald-700' : c >= 0.5 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700');
const scoreCls = (s: number) => (s >= 75 ? 'bg-[#18365E] text-white' : s >= 55 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800');
const sevBadge = (s: string) => (s === 'Critical' ? 'bg-red-100 text-red-700' : s === 'High' ? 'bg-orange-100 text-orange-700' : s === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');
const sevDot = (s: string) => (s === 'Critical' ? 'bg-red-600' : s === 'High' ? 'bg-orange-500' : s === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500');
const pct = (c: number) => Math.round(c * 100);

const REQ_ICON: Record<RequirementType, React.ComponentType<{ className?: string }>> = {
  data: BarChart3, technology: Zap, people: Users, governance: Scale,
};

function MilestoneTimeline({ milestones }: { milestones: MilestoneVM[] }) {
  const max = Math.max(...milestones.map((m) => m.months)) || 1;
  return (
    <div className="space-y-0">
      {milestones.map((m, i) => (
        <div key={m.label} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className="w-6 h-6 rounded-full bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            {i < milestones.length - 1 && <span className="w-px flex-1 bg-[#e5e7eb]" />}
          </div>
          <div className={`flex-1 min-w-0 ${i < milestones.length - 1 ? 'pb-4' : ''}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-[#18365E]">{m.label}</span>
              <span className="text-[10px] font-bold text-[#FF6E4C] bg-[#FFF1ED] rounded-full px-2 py-0.5 shrink-0">Month {m.months}</span>
            </div>
            <p className="text-[11px] text-[#4b5563] mt-0.5">{m.value}</p>
            {m.dependsOn && (
              <p className="text-[10px] text-[#717182] mt-0.5 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" />Depends on: {m.dependsOn}
              </p>
            )}
          </div>
          <div className="hidden sm:block w-28 pt-1 shrink-0">
            <div className="h-1.5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-full overflow-hidden">
              <div className="h-full bg-[#18365E] rounded-full" style={{ width: `${(m.months / max) * 100}%` }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export interface BriefTabProps {
  brief: DecisionBrief;
  onOpenRationale: () => void;
  onOpenRoi: () => void;
}

export const BriefTab: React.FC<BriefTabProps> = ({ brief, onOpenRationale, onOpenRoi }) => {
  const sc = getScores(brief);
  const fig = hubFigures(brief.id);
  const decision = parseDecisionText(brief.decisionText);
  const risks = getRisks(brief);
  const milestones = getMilestones(brief);
  const reqs = getRequirements(brief);
  const align = getAlignment(brief);

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
      {/* Main column */}
      <div className="space-y-4">
        {/* The verdict itself lives in the hub header; this card is the reasoning. The verdict pill in
            the band keeps the verdict color readable without a left stripe that would stop at the band. */}
        <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden" data-testid="brief-why-card">
          <SectionHeaderBand
            icon={<FileText className="w-3.5 h-3.5" />}
            title="Why we recommend this"
            right={<VerdictPill verdict={brief.verdict} />}
          />
          <div className="p-6">
            <p className="text-sm text-[#18365E] font-medium leading-relaxed">{decision.recommendation}</p>
            {decision.points && (
              <ol className="space-y-2.5 mt-3">
                {decision.points.map((pt, i) => (
                  <li key={pt} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-[#FF6E4C] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-sm text-[#4b5563] leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ol>
            )}
            {decision.body && <p className="text-sm text-[#4b5563] leading-relaxed mt-3">{decision.body}</p>}
          </div>
        </div>

        <BandedCard icon={<Zap className="w-3.5 h-3.5" />} title="Immediate Next Steps" accent>
          <StepList steps={brief.steps} />
        </BandedCard>

        <BandedCard icon={<Target className="w-3.5 h-3.5" />} title="Strategic Alignment">
          <div className="space-y-2.5">
            {align.map((a) => (
              <div key={a.priority} className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-[#18365E]">{a.priority}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreCls(a.score)}`}>{a.score}</span>
                </div>
                <p className="text-[11px] text-[#4b5563] leading-relaxed mt-1">{a.narrative}</p>
                <p className="text-[10px] text-[#717182] mt-1">Method: {a.method}</p>
              </div>
            ))}
          </div>
        </BandedCard>

        <Collapse icon={<BookOpen className="w-3.5 h-3.5" />} title="Strategic Context & Description">
          <p className="text-sm text-[#4b5563] leading-relaxed">{brief.context}</p>
          <p className="text-sm text-[#4b5563] leading-relaxed mt-3">{brief.description}</p>
          <p className="text-sm text-[#4b5563] leading-relaxed mt-3">
            <b className="text-[#18365E]">Operational impact:</b> {brief.opImpact}
          </p>
        </Collapse>

        <Collapse
          icon={<Shield className="w-3.5 h-3.5" />}
          title="Risk Register"
          meta={`${risks.length} identified · top severity ${risks[0]?.severity ?? 'none'}`}
        >
          <div className="space-y-3">
            {risks.map((r) => (
              <div key={r.riskId} className="border border-[#e5e7eb] rounded-lg p-3.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-[#18365E]">{r.name}</span>
                  <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${sevBadge(r.severity)}`}>{r.severity}</span>
                  <span className="text-[9px] font-semibold text-[#717182] bg-[#F5F7FA] border border-[#e5e7eb] rounded-full px-2 py-0.5">{r.category}</span>
                  <span className="text-[10px] text-[#717182] ml-auto">Likelihood: {r.likelihood}</span>
                </div>
                <p className="text-[11px] text-[#4b5563] leading-relaxed mt-1.5">{r.description}</p>
                <p className="text-[11px] text-[#4b5563] leading-relaxed mt-1">
                  <b className="text-[#18365E]">Mitigation:</b> {r.mitigation} <span className="text-[#717182]">· Owner: {r.owner}</span>
                </p>
              </div>
            ))}
          </div>
        </Collapse>

        <Collapse
          icon={<Calendar className="w-3.5 h-3.5" />}
          title="Implementation Roadmap"
          meta={`${milestones.length} milestones · dependency-aware`}
        >
          <MilestoneTimeline milestones={milestones} />
        </Collapse>

        {/* Hidden when the use case has no requirement rows (the stage-featured use cases carry none),
            rather than an empty "0 requirements" heading. */}
        {reqs.length > 0 && (
          <Collapse icon={<List className="w-3.5 h-3.5" />} title="Implementation Requirements" meta={`${reqs.length} requirements`}>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {reqs.map((q) => {
                const QIcon = REQ_ICON[q.type] ?? List;
                return (
                  <div key={q.name} className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] p-3">
                    <p className="text-[9px] font-bold uppercase tracking-wide text-[#FF6E4C] flex items-center gap-1.5">
                      <QIcon className="w-3 h-3" />{q.type}
                    </p>
                    <p className="text-xs font-bold text-[#18365E] mt-1">{q.name}</p>
                    <p className="text-[11px] text-[#4b5563] leading-relaxed mt-0.5">{q.details}</p>
                  </div>
                );
              })}
            </div>
          </Collapse>
        )}
      </div>

      {/* Rail */}
      <div className="space-y-4">
        <BandedCard
          icon={<BarChart3 className="w-3.5 h-3.5" />}
          title="Scoring Profile"
          right={
            /* Confidence pill only when the warehouse supplies one (stage client 300 has none). */
            sc.confidence !== null && sc.confidence !== undefined ? (
              <span className={`text-[9px] font-bold px-2 py-1 rounded-full ${confPill(sc.confidence)}`}>{pct(sc.confidence)}% confidence</span>
            ) : undefined
          }
        >
          <div className="mb-2.5">
            <span className="text-2xl font-bold text-[#18365E]">{sc.total}<span className="text-xs font-semibold text-[#717182]"> / 100</span></span>
          </div>
          <div className="space-y-2">
            {DIM_META.map((meta) => {
              const d = sc.dims.find((x) => x.id === meta.id);
              if (!d) return null;
              return (
                <div key={meta.id}>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-semibold text-[#18365E]">{meta.name}</span>
                    <span className="font-bold text-[#18365E]">{d.score.toFixed(1)}</span>
                  </div>
                  <div className="h-1.5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-full overflow-hidden mt-0.5">
                    <div className="h-full rounded-full" style={{ width: `${d.score}%`, background: meta.id === 'feasibility' ? '#FF6E4C' : '#18365E' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-[#717182] mt-3">#{brief.rank} of {TOTAL_PORTFOLIO_SIZE} ranked · {LIBRARY_SIZE} evaluated. <AsOfChip inline /></p>
          <button onClick={onOpenRationale} className="mt-2 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1">
            Full scoring rationale<ArrowRight className="w-3.5 h-3.5" />
          </button>
        </BandedCard>

        <BandedCard icon={<TrendingUp className="w-3.5 h-3.5" />} title="Financial Model" accent>
          {/* Payback, Net 3-Year Value and Annual Benefit live in the hub header; this card
              carries only what the header does not. */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            {([['Total 3-year cost', fig.totalCost3Yr], ['Confidence', fig.confidence]] as [string, string][]).map(([l, v]) => (
              <div key={l} className="bg-white border border-[#e5e7eb] rounded-lg px-2.5 py-2" data-testid={`brief-fin-${l === 'Confidence' ? 'confidence' : 'cost'}`}>
                <p className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide">{l}</p>
                <p className="text-sm font-bold text-[#18365E]">{v}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#717182] mb-2">Confidence is {CANON_CONFIDENCE_BASIS}.</p>
          <p className="text-[11px] text-[#4b5563] leading-relaxed">Per-year projections, benchmark bands, and every assumption traced to a source.</p>
          <button onClick={onOpenRoi} className="mt-2.5 bg-[#18365E] text-white text-xs font-semibold px-3.5 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#122a4a] transition-colors">
            <TrendingUp className="w-3.5 h-3.5" />Open ROI Model
          </button>
        </BandedCard>

        <BandedCard icon={<Shield className="w-3.5 h-3.5" />} title="Risk Digest">
          <ul className="space-y-2">
            {risks.map((r) => (
              <li key={r.riskId} className="flex items-center gap-2 text-xs text-[#4b5563]">
                <span className={`w-2 h-2 rounded-full shrink-0 ${sevDot(r.severity)}`} />
                <span className="flex-1 min-w-0 truncate">{r.name}</span>
                <span className="text-[9px] font-semibold text-[#717182] bg-[#F5F7FA] border border-[#e5e7eb] rounded-full px-1.5 py-0.5 shrink-0">{r.category}</span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-[#717182] mt-3">Full register with mitigations and owners above.</p>
        </BandedCard>

        <BandedCard icon={<Calendar className="w-3.5 h-3.5" />} title="Path to Value">
          <ol className="space-y-2">
            {milestones.slice(0, 4).map((m, i) => (
              <li key={m.label} className="flex items-center gap-2.5 text-xs">
                <span className="w-5 h-5 rounded-full bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="font-semibold text-[#18365E] flex-1 min-w-0 truncate">{m.label}</span>
                <span className="text-[#717182] shrink-0">M{m.months}</span>
              </li>
            ))}
          </ol>
        </BandedCard>
      </div>
    </div>
  );
};

export default BriefTab;
